import { useQuery } from "@tanstack/react-query"
import { authFetchJson } from "@/shared/lib/auth-fetch"

interface TeamLead {
    id: number
    fullname: string
    email: string
}

interface BackendResponse {
    success: boolean
    data: TeamLead[]
}

interface UseTeamLeadsResponse {
    data: TeamLead[] | undefined
    isLoading: boolean
    error: Error | null
}

/**
 * Hook to fetch team leads (users with Admin, BaseLeader, or TeamLeader roles) efficiently
 * @param search - Optional search term for filtering by fullname
 * @returns Team leads data with loading and error states
 */
export function useTeamLeads(search?: string): UseTeamLeadsResponse {
    const query = useQuery({
        queryKey: ['team-leads', search],
        queryFn: async (): Promise<BackendResponse> => {
            const path = search 
                ? `/user/by-role?roleId=1,2,3&search=${encodeURIComponent(search)}`
                : '/user/by-role?roleId=1,2,3'
            
            const res = await authFetchJson<TeamLead[]>(path)
            if (!res.success) {
                throw new Error(res.message || 'Failed to fetch team leads')
            }
            return {
                success: true,
                data: res.data ?? []
            }
        },
    })

    return {
        data: query.data?.success ? query.data.data : undefined,
        isLoading: query.isLoading,
        error: query.error || (query.data && !query.data.success ? new Error('Invalid response format') : null),
    }
}
